import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatMoney, getUser } from "../../lib/auth";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Home, Wallet, ClipboardCheck, Percent, Users, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_LABEL = {
  hold: "Bloqueio",
  pending: "Pendente",
  paid: "Pago",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  refunded: "Estornada",
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState({ expire: false, reminder: false });
  const user = getUser();

  async function load() {
    const r = await api.get("/admin/dashboard");
    setData(r.data);
  }
  useEffect(() => { load(); }, []);

  async function runCron(kind) {
    setRunning((s) => ({ ...s, [kind]: true }));
    try {
      const url = kind === "expire" ? "/admin/cron/expire-holds" : "/admin/cron/send-reminders";
      const { data: res } = await api.post(url);
      if (kind === "expire") {
        toast.success(res.cancelled > 0
          ? `${res.cancelled} hold(s) expirados e liberados`
          : "Nenhum hold expirado no momento");
      } else {
        toast.success(res.sent > 0
          ? `${res.sent} lembrete(s) enviado(s)`
          : "Nenhum lembrete pendente para amanhã");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao executar rotina");
    } finally {
      setRunning((s) => ({ ...s, [kind]: false }));
    }
  }

  if (!data) return <div className="text-[#6B7280]">Carregando...</div>;

  const kpis = [
    { label: "Casas publicadas", value: `${data.published_houses} / ${data.total_houses}`, icon: <Home className="w-5 h-5" /> },
    { label: "Receita do mês", value: formatMoney(data.revenue_month), icon: <Wallet className="w-5 h-5" /> },
    { label: "Ocupação hoje", value: `${data.occupancy_today}%`, icon: <Percent className="w-5 h-5" /> },
    { label: "Pagamentos p/ conferir", value: data.pending_payments, icon: <Wallet className="w-5 h-5" /> },
    { label: "Tarefas abertas", value: data.pending_tasks, icon: <ClipboardCheck className="w-5 h-5" /> },
    { label: "Reservas confirmadas", value: data.reservations_by_status.confirmed || 0, icon: <Users className="w-5 h-5" /> },
  ];

  const canRunCron = user && ["diretor", "gestor"].includes(user.role);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#6B7280]">Painel executivo</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Visão geral</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-[#E4E4E7] rounded-md p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[#3B9FD4]">{k.icon}</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B7280] mt-3">{k.label}</div>
            <div className="text-2xl font-semibold text-[#0AA89C] mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-[#E4E4E7]">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-[0.24em] text-[#6B7280]">Reservas por status</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-[#F7F4EF]">
              {Object.entries(data.reservations_by_status).map(([s, n]) => (
                <li key={s} className="flex justify-between py-2 text-sm">
                  <span className="text-[#22302E]">{STATUS_LABEL[s] || s}</span>
                  <span className="font-medium text-[#0AA89C]">{n}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-[#E4E4E7]">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-[0.24em] text-[#6B7280]">Próximas chegadas</CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcoming_reservations.length === 0 ? (
              <div className="text-sm text-[#6B7280]">Sem próximas reservas.</div>
            ) : (
              <ul className="divide-y divide-[#F7F4EF]">
                {data.upcoming_reservations.map((r) => (
                  <li key={r.id} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium text-[#0AA89C]">{r.house_name}</div>
                      <div className="text-xs text-[#6B7280]">
                        {r.guest.name} · {r.guests} hóspede(s)
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#6B7280]">Check-in</div>
                      <div className="text-[#22302E]">{r.check_in}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/admin/reservas" className="text-xs text-[#0AA89C] mt-4 inline-block hover:underline">Ver todas as reservas →</Link>
          </CardContent>
        </Card>
      </div>

      {canRunCron && (
        <Card className="border-[#E4E4E7] mt-6" data-testid="automation-panel">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-[0.24em] text-[#6B7280]">Automação</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#22302E] mb-4">
              As rotinas abaixo são executadas periodicamente pelo servidor. Utilize os botões apenas se
              precisar antecipar a execução para conferência ou testes.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                data-testid="cron-expire-btn"
                onClick={() => runCron("expire")}
                disabled={running.expire}
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                {running.expire ? "Executando..." : "Expirar holds agora"}
              </Button>
              <Button
                variant="outline"
                data-testid="cron-reminder-btn"
                onClick={() => runCron("reminder")}
                disabled={running.reminder}
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                {running.reminder ? "Executando..." : "Enviar lembretes agora"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

